import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, RollRevision, RollRevisionState } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const OFFICER_ROLES = ['REGISTRATION_OFFICER', 'ELECTORAL_COMMISSIONER'];
const STATES: RollRevisionState[] = ['SCHEDULED', 'CLAIMS_OPEN', 'OBJECTIONS_OPEN', 'REGISTRAR_REVIEW', 'CERTIFIED', 'CLOSED'];

/** GET/POST /api/v1/m39/roll-revisions — the statutory claims/objections revision cycle. */
export function RollRevisions() {
  const { persona } = useAuth();
  const isOfficer = persona?.roles.some((r) => OFFICER_ROLES.includes(r)) ?? false;
  const navigate = useNavigate();
  const [status, setStatus] = useState<RollRevisionState | ''>('');
  const [showCreate, setShowCreate] = useState(false);

  const params = new URLSearchParams({ limit: '50' });
  if (status) params.set('status', status);
  const listQ = useApiQuery<Page<RollRevision>>(`/api/v1/m39/roll-revisions?${params.toString()}`);

  return (
    <div>
      <PageHeader
        title="Roll Revisions"
        subtitle="Statutory claims and objections cycles that maintain the electoral roll."
        actions={
          isOfficer ? (
            <button className="btn-primary" onClick={() => setShowCreate(true)}>
              + Open revision
            </button>
          ) : undefined
        }
      />
      {!isOfficer ? <RoleNotice roles={OFFICER_ROLES} /> : null}

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="State" className="!mb-0">
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as RollRevisionState | '')}>
            <option value="">All</option>
            {STATES.map((s) => (
              <option key={s} value={s}>
                {s.replaceAll('_', ' ')}
              </option>
            ))}
          </select>
        </Field>
      </div>

      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No roll revisions match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Title</th>
                <th className="table-head">Year</th>
                <th className="table-head">State</th>
                <th className="table-head">Certified</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((r) => (
                <tr key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/elections/roll-revisions/${r.id}`)}>
                  <td className="table-cell font-medium">{r.title}</td>
                  <td className="table-cell text-xs">{r.year}</td>
                  <td className="table-cell">
                    <StatusBadge status={r.state} />
                  </td>
                  <td className="table-cell text-xs">{r.certifiedBy ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate ? (
        <CreateRevisionModal
          onClose={() => setShowCreate(false)}
          onDone={(created) => {
            setShowCreate(false);
            navigate(`/elections/roll-revisions/${created.id}`);
          }}
        />
      ) : null}
    </div>
  );
}

function CreateRevisionModal({ onClose, onDone }: { onClose: () => void; onDone: (r: RollRevision) => void }) {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<RollRevision>('/api/v1/m39/roll-revisions', { year: Number.parseInt(year, 10), title });
      onDone(created);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to open revision');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Open a roll revision cycle" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Year">
          <input type="number" className="field-input" value={year} onChange={(e) => setYear(e.target.value)} />
        </Field>
        <Field label="Title">
          <input className="field-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Annual Roll Revision 2026" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || title.trim().length < 3}>
            {busy ? 'Opening…' : 'Open revision'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
